import Vue from 'vue';
import {getInstance} from '@snapshot-labs/lock/plugins/vue';
import {formatEther, parseEther} from '@ethersproject/units';
import {abiEncode, abiDecode} from '@/helpers/content';
import getProvider from '@/helpers/provider';
import {formatSpace} from '@/helpers/utils';
import {getBlockNumber, getBlockTimestamp, getContract, sendTransaction} from '@/helpers/web3';
import {Contract, Provider} from 'ethers-multicall';
import {getAddress} from '@ethersproject/address';
import abi from '@/helpers/abi';
const {createClient} = require('graphqurl');

//The test network can only query 5000 blocks
const fromBlockOrBlockHash = 18636080;
const toBlock = 18636858;

const state = {
    init: false,
    loading: false,
    spaces: {},
    language: 'en' // en || zh-CN
};

const proposalState = {
    '0': 'Pending',
    '1': 'Active',
    '2': 'Canceled',
    '3': 'Defeated',
    '4': 'Succeeded',
    '5': 'Queued',
    '6': 'Expired',
    '7': 'Executed'
};

const spaces = {
    '56': {
        domain: 'https://gov.gyro.money/',
        name: 'Gyro',
        network: '56',
        skin: 'yearn',
        symbol: 'veGyro',
        token: '0xeB84193D6d8ebfEd5848517a923b8DC84F3De0A7',
        governor: '0xB67dF844C37bEa88e9aC0C3Fdc3D5c1B7E8dB6a2',
        logsFromBlock: 1,
        members: [],
        strategies: [],
        secondsPerBlock: 3
    },
    '97': {
        domain: 'https://dev.gov.gyro.money/',
        name: 'Gyro',
        network: '97',
        skin: 'yearn',
        symbol: 'veGyro',
        token: '0x473aa21e30c59ac294bc61e4a82516b21a5c9742',
        governor: '0xcEC3b917aC71F32bbB5B78517bB9fDEde2192094',
        logsFromBlock: 18619680,
        members: [],
        strategies: [],
        secondsPerBlock: 3
    }
};

const graphqlClientUrl = {
    56: '',
    97: ''
};

const mutations = {
    SET_LANGUAGE: (state, language) => {
        state.language = language;
    },
    SET(_state, payload) {
        Object.keys(payload).forEach(key => {
            Vue.set(_state, key, payload[key]);
        });
    },
    SEND_REQUEST() {
        console.debug('SEND_REQUEST');
    },
    SEND_SUCCESS() {
        console.debug('SEND_SUCCESS');
    },
    SEND_FAILURE(_state, payload) {
        console.debug('SEND_FAILURE', payload);
    },
    GET_USER_LAST_PROPOSAL_REQUEST() {
        console.debug('GET_USER_LAST_PROPOSAL_REQUEST');
    },
    GET_USER_LAST_PROPOSAL_SUCCESS() {
        console.debug('GET_USER_LAST_PROPOSAL_SUCCESS');
    },
    GET_USER_LAST_PROPOSAL_FAILURE() {
        console.debug('GET_USER_LAST_PROPOSAL_FAILURE');
    },
    GET_PROPOSAL_STATE_REQUEST() {
        console.debug('GET_PROPOSAL_STATE_REQUEST');
    },
    GET_PROPOSAL_STATE_SUCCESS() {
        console.debug('GET_PROPOSAL_STATE_SUCCESS');
    },
    GET_PROPOSAL_ACTIONS_REQUEST() {
        console.debug('GET_PROPOSAL_ACTIONS_REQUEST');
    },
    GET_PROPOSAL_ACTIONS_SUCCESS() {
        console.debug('GET_PROPOSAL_ACTIONS_SUCCESS');
    },
    GET_PROPOSAL_ACTIONS_FAILURE() {
        console.debug('GET_PROPOSAL_ACTIONS_FAILURE');
    },
    GET_PROPOSAL_STATE_FAILURE() {
        console.debug('GET_PROPOSAL_STATE_FAILURE');
    },
    GET_GOVERNOR_PARAMS_REQUEST() {
        console.debug('GET_GOVERNOR_PARAMS_REQUEST');
    },
    GET_GOVERNOR_PARAMS_SUCCESS() {
        console.debug('GET_GOVERNOR_PARAMS_SUCCESS');
    },
    GET_GOVERNOR_PARAMS_FAILURE() {
        console.debug('GET_GOVERNOR_PARAMS_FAILURE');
    },
    // GET_DELEGATEE_REQUEST() {
    //     console.debug('GET_DELEGATEE_REQUEST');
    // },
    GET_DELEGATEE_SUCCESS() {
        console.debug('GET_DELEGATEE_SUCCESS');
    },
    GET_DELEGATEE_FAILURE() {
        console.debug('GET_DELEGATEE_FAILURE');
    },
    GET_PROPOSALS_REQUEST() {
        console.debug('GET_PROPOSALS_REQUEST');
    },
    GET_PROPOSALS_SUCCESS() {
        console.debug('GET_PROPOSALS_SUCCESS');
    },
    GET_PROPOSALS_FAILURE(_state, payload) {
        console.debug('GET_PROPOSALS_FAILURE', payload);
    },
    GET_PROPOSAL_REQUEST() {
        console.debug('GET_PROPOSAL_REQUEST');
    },
    GET_PROPOSAL_SUCCESS() {
        console.debug('GET_PROPOSAL_SUCCESS');
    },
    GET_PROPOSAL_FAILURE(_state, payload) {
        console.debug('GET_PROPOSAL_FAILURE', payload);
    },
    GET_USER_TOKENIDS_REQUEST() {
        console.debug('GET_USER_TOKENIDS_REQUEST');
    },
    GET_USER_TOKENIDS_SUCCESS() {
        console.debug('GET_USER_TOKENIDS_SUCCESS');
    },
    GET_USER_TOKENIDS_FAILURE(_state, payload) {
        console.debug('GET_USER_TOKENIDS_FAILURE', payload);
    },
    GET_POWER_REQUEST() {
        console.debug('GET_POWER_REQUEST');
    },
    GET_POWER_SUCCESS() {
        console.debug('GET_POWER_SUCCESS');
    },
    GET_POWER_FAILURE(_state, payload) {
        console.debug('GET_POWER_FAILURE', payload);
    }
};

const actions = {
    getSpaces: async ({commit}) => {
        const newSpaces = Object.fromEntries(
            Object.entries(spaces).map(space => [space[0], formatSpace(space[0], space[1])])
        );
        commit('SET', {spaces: newSpaces});
        return newSpaces;
    },
    setLanguage({commit}, language) {
        commit('SET_LANGUAGE', language);
    },
    init: async ({commit, dispatch}) => {
        commit('SET', {loading: true});
        // const connector = await Vue.prototype.$auth.getConnector();
        // if (connector) await dispatch('login', connector);
        await dispatch('getSpaces');
        commit('SET', {loading: false, init: true});
    },
    loading: ({commit}, payload) => {
        commit('SET', {loading: payload});
    },
    decode: ({commit}, payload) => {
        return abiDecode(payload.types, payload.values);
    },
    encode: ({commit}, payload) => {
        return abiEncode(payload.types, payload.values);
    },
    formatEther: async ({commit}, amount) => {
        return await formatEther(amount);
    },
    parseEther: async ({commit}, amount) => {
        return await parseEther(amount);
    },
    send: async ({commit, dispatch}, {type, payload}) => {
        const auth = getInstance();
        commit('SEND_REQUEST');
        try {
            const result: any = await sendTransaction(auth.web3, [
                payload.contractType,
                payload.contractAddress,
                payload.action,
                [...payload.args]
            ]);
            console.log('tx: ', result);
            commit('SEND_SUCCESS');
            dispatch('notify', ['green', `Your ${type} is in!`]);

            return true;
        } catch (e) {
            commit('SEND_FAILURE', e);
            const errorMessage =
                e && e.error_description
                    ? `Oops, ${e.error_description}`
                    : 'Oops, something went wrong!';
            dispatch('notify', ['red', errorMessage]);
            return false;
        }
    },
    getLatestProposalIds: async ({commit}, space) => {
        commit('GET_USER_LAST_PROPOSAL_REQUEST');
        try {
            const auth = getInstance();
            const accounts = await auth.web3.listAccounts();
            const provider = getProvider(space.network);
            const contract = await getContract(space.governor, 'Governor', provider);
            const proposalId = await contract.latestProposalIds(accounts[0]);
            commit('GET_USER_LAST_PROPOSAL_SUCCESS');
            return proposalId.toString();
        } catch (e) {
            commit('GET_USER_LAST_PROPOSAL_FAILURE', e);
        }
    },
    getProposalState: async ({commit}, {space, id}) => {
        commit('GET_PROPOSAL_STATE_REQUEST');
        try {
            const provider = getProvider(space.network);
            const contract = await getContract(space.governor, 'Governor', provider);
            const stateId = await contract.state(id);
            commit('GET_PROPOSAL_STATE_SUCCESS');
            return proposalState[stateId];
        } catch (e) {
            console.log(e);
            commit('GET_PROPOSAL_STATE_FAILURE', e);
        }
    },
    getProposalActions: async ({commit}, {space, id}) => {
        commit('GET_PROPOSAL_ACTIONS_REQUEST');
        try {
            const provider = getProvider(space.network);
            const contract = await getContract(space.governor, 'Governor', provider);
            const params = await contract.getActions(id);
            commit('GET_PROPOSAL_ACTIONS_SUCCESS');
            return params;
        } catch (e) {
            console.log(e);
            commit('GET_PROPOSAL_ACTIONS_FAILURE', e);
        }
    },
    getGovernorParams: async ({commit}, space) => {
        commit('GET_GOVERNOR_PARAMS_REQUEST');
        try {
            const auth = getInstance();
            const governorContract = await getContract(space.governor, 'Governor', auth.web3);
            const proposalMaxOperations = await governorContract.proposalMaxOperations();
            const threshold = await governorContract.proposalThreshold();
            commit('GET_GOVERNOR_PARAMS_SUCCESS');
            return {
                proposalMaxOperations: proposalMaxOperations.toString(),
                proposalThreshold: (threshold / 10 ** 9).toString()
            };
        } catch (e) {
            console.error(e);
            commit('GET_GOVERNOR_PARAMS_FAILURE', e);
        }
    },
    getProposals: async ({commit}, space) => {
        commit('GET_PROPOSALS_REQUEST');
        if (!space) return null;

        try {
            const provider = getProvider(space.network);
            const ethcallProvider = new Provider(provider);
            await ethcallProvider.init();
            const curBlockNumber = await getBlockNumber(provider);
            const curTimestamp = await getBlockTimestamp(provider, curBlockNumber);
            const govMulticallContract = new Contract(getAddress(space.governor), abi['Governor']);
            const govContract = await getContract(space.governor, 'Governor', provider);
            let proposals: any = [];
            let proposalLogs: any = [];
            if (graphqlClientUrl[space.network]) {
                const client = createClient({
                    endpoint: graphqlClientUrl[space.network]
                });
                const logs = await client.query({
                    query: `
                        query ($name: String) {
                            proposalCreateds {
                                id
                                proposalId
                                proposer
                                startBlock
                                endBlock
                                description
                            }
                        }
                      `
                });

                proposalLogs = logs.data.proposalCreateds;
            } else {
                const event = govContract.filters.ProposalCreated();
                proposalLogs = await govContract.queryFilter(event, fromBlockOrBlockHash, toBlock);
            }
            proposals = await Promise.all(
                proposalLogs.map(async logData => {
                    let description: any,
                        proposer: any,
                        id: any,
                        proposalId: any,
                        startBlock: any,
                        endBlock: any;
                    if (graphqlClientUrl[space.network]) {
                        description = logData.description;
                        proposer = logData.proposer;
                        proposalId = logData.proposalId;
                        id = logData.id;
                        startBlock = logData.startBlock;
                        endBlock = logData.endBlock;
                    } else {
                        description = logData.args.description;
                        proposer = logData.args.proposer;
                        id = logData.transactionHash;
                        proposalId = logData.args.id;
                        startBlock = logData.args.startBlock;
                        endBlock = logData.args.endBlock;
                    }

                    let startTimestamp, endTimestamp;
                    if (curBlockNumber > startBlock) {
                        startTimestamp = await getBlockTimestamp(provider, parseFloat(startBlock));
                    } else {
                        startTimestamp =
                            curTimestamp + (startBlock - curBlockNumber) * space.secondsPerBlock;
                    }

                    if (curBlockNumber > endBlock) {
                        endTimestamp = await getBlockTimestamp(provider, parseFloat(endBlock));
                    } else {
                        endTimestamp =
                            curTimestamp + (endBlock - curBlockNumber) * space.secondsPerBlock;
                    }

                    const [stateId] = await ethcallProvider.all([
                        govMulticallContract.state(proposalId)
                    ]);

                    return {
                        id: proposalId.toString(),
                        address: proposer,
                        msg: {
                            payload: {
                                transactionHash: id,
                                name: description,
                                start: startTimestamp,
                                end: endTimestamp,
                                state: proposalState[stateId]
                            }
                        }
                    };
                })
            );

            commit('GET_PROPOSALS_SUCCESS');
            return proposals.sort(function(a, b) {
                return b.id - a.id;
            });
        } catch (e) {
            commit('GET_PROPOSALS_FAILURE', e);
        }
    },
    getReceipt: async ({commit}, payload) => {
        try {
            const auth = getInstance();
            const accounts = await auth.web3.listAccounts();
            const contract = await getContract(payload.space.governor, 'Governor', auth.web3);
            const receipt = await contract.getReceipt(payload.id, accounts[0]);

            return receipt;
        } catch (error) {
            return {
                hasVoted: false
            };
        }
    },
    getProposal: async ({commit}, payload) => {
        commit('GET_PROPOSAL_REQUEST');
        try {
            const provider = getProvider(payload.space.network);
            const ethcallProvider = new Provider(provider);
            await ethcallProvider.init();

            const blockNumber = await getBlockNumber(provider);
            const curTimestamp = await getBlockTimestamp(provider, blockNumber);

            // const contract = new Contract(getAddress(payload.space.governor), abi['Governor']);
            const govMulticallContract = new Contract(
                getAddress(payload.space.governor),
                abi['Governor']
            );
            const govContract = await getContract(payload.space.governor, 'Governor', provider);

            const result: any = {};

            const [proposal, stateId] = await ethcallProvider.all([
                govMulticallContract.proposals(payload.id),
                govMulticallContract.state(payload.id)
            ]);

            let startTimestamp, endTimestamp;
            if (blockNumber > proposal.startBlock) {
                startTimestamp = await getBlockTimestamp(provider, parseFloat(proposal.startBlock));
            } else {
                startTimestamp =
                    curTimestamp +
                    (proposal.startBlock - blockNumber) * payload.space.secondsPerBlock;
            }

            if (blockNumber > proposal.endBlock) {
                endTimestamp = await getBlockTimestamp(provider, parseFloat(proposal.endBlock));
            } else {
                endTimestamp =
                    curTimestamp +
                    (proposal.endBlock - blockNumber) * payload.space.secondsPerBlock;
            }

            result.proposal = {
                address: proposal.proposer,
                id: proposal.id,
                msg: {
                    token: payload.space.governor,
                    type: 'proposal',
                    payload: {
                        choices: ['Yes', 'No', 'Abstain'],
                        startBlock: proposal.startBlock,
                        endBlock: proposal.endBlock,
                        start: startTimestamp,
                        end: endTimestamp,
                        state: proposalState[stateId],
                        eta: proposal.eta
                    }
                }
            };

            if (graphqlClientUrl[payload.space.network]) {
                const client = createClient({
                    endpoint: graphqlClientUrl[payload.space.network]
                });

                const proposalLogs = await client.query({
                    query: `
                        query ($name: String) {
                            proposalCreateds(where: { proposalId: ${payload.id} }) {
                                id
                                proposalId
                                proposer
                                startBlock
                                endBlock
                                description
                            }
                        }
                      `
                });

                result.proposal.msg.payload.name = proposalLogs
                    ? proposalLogs.data.proposalCreateds[0].description
                    : '';
                result.proposal.msg.payload.transactionHash = proposalLogs
                    ? proposalLogs.data.proposalCreateds[0].id
                    : '';

                const voteLogs = await client.query({
                    query: `
                        query ($name: String) {
                            voteCasts(where: { proposalId: ${payload.id} }) {
                                id,
                                voter,
                                proposalId,
                                support,
                                votes
                            }
                        }
                      `
                });

                result.votes = await Promise.all(
                    voteLogs.data.voteCasts.map(async logData => {
                        const {voter, proposalId, support, votes} = logData;
                        if (proposalId.toString() === payload.id) {
                            return {
                                address: voter,
                                msg: {
                                    type: 'vote',
                                    payload: {
                                        choice: support ? 1 : 2
                                    }
                                },
                                scores: [votes / 10 ** 9],
                                balance: votes / 10 ** 9
                            };
                        }
                    })
                );
            } else {
                const proposalEvent = govContract.filters.ProposalCreated();
                const proposalLogs = await govContract.queryFilter(
                    proposalEvent,
                    fromBlockOrBlockHash,
                    toBlock
                );
                for (const logData of proposalLogs) {
                    const proposalId = logData?.args?.id;
                    if (proposalId.toString() === payload.id) {
                        result.proposal.msg.payload.name = logData?.args?.description;
                        result.proposal.msg.payload.transactionHash = logData?.transactionHash;
                        break;
                    }
                }

                const voteEvent = govContract.filters.VoteCast();
                const voteLogs = await govContract.queryFilter(
                    voteEvent,
                    fromBlockOrBlockHash,
                    toBlock
                );

                result.votes = voteLogs.map(logData => {
                    const voter = logData?.args?.voter;
                    const proposalId = logData?.args?.proposalId;
                    const support = logData?.args?.support;
                    const votes = logData?.args?.votes;
                    if (proposalId.toString() === payload.id) {
                        return {
                            address: voter,
                            msg: {
                                type: 'vote',
                                payload: {
                                    choice: support == 1 ? 1 : support == 0 ? 2 : 3
                                }
                            },
                            scores: [votes / 10 ** 9],
                            balance: votes / 10 ** 9
                        };
                    }
                });
            }
            result.votes = result.votes?.filter(res => res != undefined);
            const forVotes = proposal.forVotes / 10 ** 9,
                abstainVotes = proposal.abstainVotes / 10 ** 9,
                againstVotes = proposal.againstVotes / 10 ** 9;

            result.results = {
                totalBalances: [forVotes, againstVotes, abstainVotes],
                totalScores: [forVotes, againstVotes, abstainVotes],
                totalVotesBalances:
                    parseFloat(forVotes.toString()) +
                    parseFloat(againstVotes.toString()) +
                    parseFloat(abstainVotes.toString())
            };

            commit('GET_PROPOSAL_SUCCESS');
            return result;
        } catch (e) {
            commit('GET_PROPOSAL_FAILURE', e);
        }
    },
    getTokenIds: async ({commit}, {space, account}) => {
        commit('GET_USER_TOKENIDS_REQUEST');
        try {
            commit('GET_USER_TOKENIDS_SUCCESS');
            const provider = getProvider(space.network);
            const veContract = await getContract(space.token, 'veGyro', provider);
            const count = await veContract.balanceOf(account);
            const ids: any = [];
            for (let i = 0; i < parseInt(count.toString()); i++) {
                const id = await veContract.tokenOfOwnerByIndex(account, i);
                ids.push(parseInt(id.toString()));
            }
            return ids;
        } catch (e) {
            commit('GET_USER_TOKENIDS_FAILURE', e);
        }
    },
    getPower: async ({commit}, {space, tokenId, blockNumber}) => {
        commit('GET_POWER_REQUEST');
        try {
            const provider = getProvider(space.network);
            const contract = await getContract(space.token, 'veGyro', provider);
            if (!blockNumber) {
                blockNumber = (await getBlockNumber(getProvider(space.network))) - 1;
            }
            const balance = await contract.balanceOfNFT(tokenId);

            const scores: any = [balance / 10 ** 9];
            commit('GET_POWER_SUCCESS');
            return {
                scores,
                totalScore: scores.reduce((a, b: any) => a + b, 0)
            };
        } catch (e) {
            commit('GET_POWER_FAILURE', e);
            return {
                scores: [],
                totalScore: 0
            };
        }
    }
};

export default {
    state,
    mutations,
    actions
};
